/**
 * /academy/home: urgent session mock exams, the next thing to do, the goal, the four skills as
 * activity rings, placement and study plan, practice, plan, history.
 */

import {
  ApiError,
  type Attempt,
  type AttemptModeEnum,
  type Learner,
  type SkillsEnum,
  getAcademyAttemptsListQueryKey,
  useAcademyAttemptsCreate,
  useAcademyAttemptsList,
  useAcademyFormatRetrieve,
  useAcademyMeRetrieve,
  useAcademyProgressRetrieve,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SegmentedControl, Skeleton, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Compass,
  FileDown,
  MapPin,
  PencilLine,
  Play,
  RotateCcw,
  Sprout,
  Target,
  Timer as TimerIcon,
  Trophy,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { BeginnerNext } from "./AgencyName";
import { CenterCard } from "./CenterPicker";
import { ExamDatesCard } from "./ExamDatesCard";
import { GoalForm, PlansSection, goalsMissing } from "./HomeSections";
import { CourseEntries } from "./ielts/CourseEntries";
import { useOnlyExam } from "./ielts/exam";
import { parseProgress, parseStructure } from "./progressTypes";
import { SkillChips, dateText, daysUntil } from "./shared";
import { SKILLS, type Skill, isSkill, parsePlacement, parseResults } from "./types";
import { ActivityRings, CardTitle, SectionTitle, SkillIcon } from "./ui";
import { HomeExtras } from "./coaching/HomeExtras";
import { RenewalBanner } from "./sales/RenewalBanner";
import { UrgentMocks } from "./UrgentMocks";
import { InMyLanguage } from "./ielts/InMyLanguage";
import { MotherTonguePrompt } from "./ielts/MotherTongue";

function useStart() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = useAcademyAttemptsCreate();
  const start = (kind: Attempt["kind"], skills: SkillsEnum[], mode: AttemptModeEnum = "exam") =>
    create.mutate(
      { data: { kind, skills, mode } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey() });
          navigate(`/academy/attempt/${response.data.id}`);
        },
      },
    );
  const error = create.error instanceof ApiError ? (create.error.detail ?? null) : create.error ? "" : null;
  return { start, pending: create.isPending, error };
}

/** Estimated NCLC per skill: the latest analysis when there is one, else the placement. */
function useSkillLevels(learner: Learner): Partial<Record<Skill, number>> {
  const progress = useAcademyProgressRetrieve(undefined, { query: { retry: false, staleTime: 60_000 } });
  return useMemo(() => {
    const out: Partial<Record<Skill, number>> = {};
    const data = progress.data?.status === 200 ? parseProgress(progress.data.data) : null;
    const placement = parsePlacement(learner.placement);
    for (const skill of SKILLS) {
      const fromAnalysis = data?.analysis.skills[skill].nclc ?? null;
      const fromPlacement = placement?.results[skill]?.nclc;
      const value = fromAnalysis ?? fromPlacement;
      if (typeof value === "number") out[skill] = value;
    }
    return out;
  }, [progress.data, learner.placement]);
}

function SkillRingsCard({ learner }: { learner: Learner }) {
  const { t } = useTranslation();
  const levels = useSkillLevels(learner);
  const target = learner.target_nclc ?? 7;
  const measured = SKILLS.filter((s) => levels[s] !== undefined);
  return (
    <GlassCard as="section" className="ac-card ac-rings-card" aria-labelledby="ac-rings-title">
      <div className="ac-card__head">
        <CardTitle id="ac-rings-title" icon={<Target size={16} />}>
          {t("academy.ui.home.ringsTitle")}
        </CardTitle>
        <Link to="/academy/progress" className="ac-more">
          {t("academy.ui.home.seeProgress")}
          <ChevronRight aria-hidden size={16} />
        </Link>
      </div>
      {measured.length === 0 ? (
        <div className="ac-rings-card__empty">
          <ActivityRings rings={SKILLS.map((skill) => ({ skill, ratio: 0 }))} size={132} stroke={11} />
          <p className="ac-secondary">{t("academy.ui.home.ringsEmpty")}</p>
        </div>
      ) : (
        <div className="ac-rings-card__body">
          <ActivityRings
            rings={SKILLS.map((skill) => ({ skill, ratio: (levels[skill] ?? 0) / target }))}
            size={168}
            stroke={14}
          />
          <ul className="ac-ring-legend">
            {SKILLS.map((skill) => (
              <li key={skill} className={`ac-skill--${skill}`}>
                <span className="ac-ring-legend__swatch" aria-hidden="true" />
                <span className="ac-ring-legend__name">{t(`academy.skill.${skill}`)}</span>
                <span className="ac-ring-legend__value ao-num">
                  {levels[skill] !== undefined
                    ? t("academy.ui.home.ringValue", { n: levels[skill], target })
                    : t("academy.progress.notMeasured")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="ac-footnote">{t("academy.ui.home.ringsHint", { n: target })}</p>
    </GlassCard>
  );
}

function GoalCard({ learner, onEdit }: { learner: Learner; onEdit: () => void }) {
  const { t, i18n } = useTranslation();
  const target = learner.target_nclc ?? 7;
  const days = daysUntil(learner.exam_date);
  return (
    <GlassCard as="section" className="ac-card ac-goal" aria-labelledby="ac-goal-title">
      <div className="ac-card__head">
        <CardTitle id="ac-goal-title" icon={<Trophy size={16} />} tone="warning">
          {t("academy.ui.home.goalTitle")}
        </CardTitle>
        <Button variant="ghost" size="sm" icon={<PencilLine aria-hidden size={15} />} onClick={onEdit}>
          {t("academy.goals.edit")}
        </Button>
      </div>
      <div className="ac-goal__figures">
        <div className="ac-goal__figure">
          <span className="ac-goal__label">{t("academy.progress.target")}</span>
          <span className="ac-goal__value ao-num">{t("academy.goals.nclc", { n: target })}</span>
        </div>
        <div className="ac-goal__figure">
          <span className="ac-goal__label">{t("academy.ui.home.examIn")}</span>
          <span className="ac-goal__value ao-num">
            {days === null ? "—" : days >= 0 ? t("academy.ui.home.days", { count: days }) : t("academy.ui.home.examPast")}
          </span>
        </div>
      </div>
      <ul className="ac-meta-list">
        <li>
          <CalendarDays aria-hidden size={15} />
          <span>{learner.exam_date ? dateText(learner.exam_date, i18n.language) : t("academy.progress.notSet")}</span>
        </li>
        <li>
          <MapPin aria-hidden size={15} />
          <span>{learner.exam_center || t("academy.progress.notSet")}</span>
        </li>
      </ul>
      <div className="ac-goal__ready">
        {learner.exam_ready ? (
          <>
            <Badge tone="success">{t("academy.ready.yes")}</Badge>
            <span className="ac-secondary">{t("academy.ready.yesText", { n: target })}</span>
          </>
        ) : (
          <>
            <Badge>{t("academy.ready.no")}</Badge>
            <span className="ac-secondary">{t("academy.ready.noText", { n: target })}</span>
          </>
        )}
      </div>
    </GlassCard>
  );
}

export function AcademyHome() {
  const { t, i18n } = useTranslation();
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  useOnlyExam(me.data?.status === 200 ? me.data.data.exams : undefined);
  const attempts = useAcademyAttemptsList();
  const [editGoals, setEditGoals] = useState(false);
  const [practiceSkill, setPracticeSkill] = useState<Skill>("co");
  const [practiceMode, setPracticeMode] = useState<AttemptModeEnum>("training");
  const { start, pending, error } = useStart();
  const format = useAcademyFormatRetrieve(undefined, { query: { staleTime: 3_600_000, retry: false } });
  const mockMinutes = useMemo(() => {
    const structure = parseStructure(format.data?.status === 200 ? format.data.data.sections : undefined);
    return SKILLS.reduce((sum, skill) => sum + (structure[skill]?.minutes ?? 0), 0);
  }, [format.data]);

  if (me.isLoading) {
    return (
      <div className="ac-page">
        <Skeleton style={{ height: 64, maxWidth: 360 }} />
        <div className="ac-grid">
          <Skeleton className="ac-span-7" style={{ height: 220 }} />
          <Skeleton className="ac-span-5" style={{ height: 220 }} />
        </div>
      </div>
    );
  }
  if (me.error instanceof ApiError && me.error.status === 404) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <h1 className="ac-title-2">{t("academy.home.noSpaceTitle")}</h1>
        <p className="ac-secondary">{t("academy.home.noSpace")}</p>
      </GlassCard>
    );
  }
  const learner = me.data?.status === 200 ? me.data.data : undefined;
  if (!learner) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void me.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }

  const rows = attempts.data?.status === 200 ? attempts.data.data : [];
  const placement = parsePlacement(learner.placement);
  const placementAttempt = rows.find((a) => a.kind === "placement" && a.status === "submitted");
  const placementOpen = rows.find((a) => a.kind === "placement" && a.status === "in_progress");
  const openAttempt = rows.find((a) => a.kind !== "placement" && a.status === "in_progress");
  const firstName = learner.full_name.split(" ")[0] || learner.email;
  const locked = !learner.has_access;
  const missing = goalsMissing(learner);
  const target = learner.target_nclc ?? 7;

  /** The single most useful thing to do now, as a bright card. */
  const next = (() => {
    if (placementOpen) {
      return {
        eyebrow: t("academy.placement.title"),
        title: t("academy.placement.inProgress"),
        action: (
          <Link className={`${buttonClass("secondary", { size: "lg" })} ac-hero-card__cta`} to={`/academy/attempt/${placementOpen.id}`}>
            <RotateCcw aria-hidden size={18} />
            {t("academy.placement.resume")}
          </Link>
        ),
      };
    }
    if (!placement && !placementAttempt && !learner.beginner) {
      return {
        eyebrow: t("academy.ui.home.nextFree"),
        title: t("academy.placement.title"),
        text: t("academy.placement.intro"),
        action: (
          <Button size="lg" variant="secondary" className="ac-hero-card__cta" disabled={pending} onClick={() => start("placement", [...SKILLS])}>
            <Compass aria-hidden size={18} />
            {t("academy.placement.start")}
          </Button>
        ),
      };
    }
    if (openAttempt) {
      return {
        eyebrow: t("academy.ui.home.nextTitle"),
        title: t("academy.ui.home.resumeTitle", { kind: t(`academy.kind.${openAttempt.kind}`) }),
        text: t("academy.player.saved"),
        action: (
          <Link className={`${buttonClass("secondary", { size: "lg" })} ac-hero-card__cta`} to={`/academy/attempt/${openAttempt.id}`}>
            <RotateCcw aria-hidden size={18} />
            {t("academy.history.resume")}
          </Link>
        ),
      };
    }
    if (locked) {
      return {
        eyebrow: t("academy.ui.home.nextTitle"),
        title: t("academy.ui.home.unlockTitle"),
        text: t("academy.train.locked"),
        action: (
          <Link className={`${buttonClass("secondary", { size: "lg" })} ac-hero-card__cta`} to="/academy/plans">
            {t("academy.plans.choose")}
            <ArrowRight aria-hidden size={18} />
          </Link>
        ),
      };
    }
    return {
      eyebrow: t("academy.ui.home.nextTitle"),
      title: t("academy.ui.home.mockTitle"),
      text: t("academy.train.mockHint"),
      action: (
        <Link className={`${buttonClass("secondary", { size: "lg" })} ac-hero-card__cta`} to="/academy/mocks">
          <Play aria-hidden size={18} />
          {t("academy.ui.home.mockCta")}
        </Link>
      ),
    };
  })();

  return (
    <div className="ac-page">
      <header className="ac-head">
        <div className="ac-head__text">
          <p className="ac-eyebrow">{t("academy.ui.home.today", { date: dateText(new Date().toISOString(), i18n.language) })}</p>
          <h1 className="ac-head__title">{t("academy.home.hello", { name: firstName })}</h1>
          <p className="ac-head__subtitle">
            {t("academy.home.target", { n: target })}
            {learner.exam_date ? ` · ${t("academy.home.examOn", { date: dateText(learner.exam_date, i18n.language) })}` : ""}
          </p>
        </div>
      </header>

      <RenewalBanner />
      <HomeExtras />

      <MotherTonguePrompt />

      <UrgentMocks locked={locked} />
      <ExamDatesCard exam="tcf_canada" />

      <CourseEntries exam="tcf_canada" beginner={learner.beginner} />

      {learner.beginner && (
        <GlassCard as="section" className="ac-card ac-card--success" aria-labelledby="academy-beginner">
          <CardTitle id="academy-beginner" icon={<Sprout size={16} />} tone="success">
            {t("academy.beginner.title")}
          </CardTitle>
          <p>{t("academy.beginner.text")}</p>
          <BeginnerNext />
        </GlassCard>
      )}

      {missing || editGoals ? <GoalForm me={learner} onDone={missing ? undefined : () => setEditGoals(false)} /> : null}
      {!missing && !editGoals && !learner.exam_center && <CenterCard me={learner} />}

      <div className="ac-grid">
        {next && (
          <section className="ac-hero-card ac-span-7" aria-labelledby="ac-next-title">
            <div className="ac-hero-card__glow" aria-hidden="true" />
            <p className="ac-hero-card__eyebrow">{next.eyebrow}</p>
            <h2 id="ac-next-title" className="ac-hero-card__title">
              {next.title}
            </h2>
            {next.text && <p className="ac-hero-card__text">{next.text}</p>}
            <InMyLanguage text={[next.eyebrow, next.title, next.text].filter(Boolean).join(" — ")} source={i18n.language.startsWith("en") ? "en" : "fr"} examLanguage="fr" kind="home" />
            <div className="ac-hero-card__actions">{next.action}</div>
          </section>
        )}
        {!missing && !editGoals && (
          <div className="ac-span-5">
            <GoalCard learner={learner} onEdit={() => setEditGoals(true)} />
          </div>
        )}
      </div>

      <div className="ac-grid">
        <div className="ac-span-5">
          <SkillRingsCard learner={learner} />
        </div>
        {(placement || (placementAttempt && !learner.beginner)) && (
          <GlassCard as="section" className="ac-card ac-span-7" aria-labelledby="academy-placement">
            <div className="ac-card__head">
              <CardTitle id="academy-placement" icon={<Compass size={16} />} tone="info">
                {t("academy.placement.title")}
              </CardTitle>
              {placement && <span className="ac-caption">{t("academy.placement.done", { date: dateText(placement.at, i18n.language) })}</span>}
            </div>
            {placement ? (
              <>
                <SkillChips results={placement.results} />
                {placement.plan && (
                  <div className="ac-plan-box">
                    <p className="ac-plan-box__lead">
                      {placement.plan.weeks > 0
                        ? t("academy.placement.weeks", {
                            count: placement.plan.weeks,
                            hours: placement.plan.hoursPerWeek,
                            target,
                          })
                        : t("academy.placement.atTarget")}
                    </p>
                    {placement.plan.priorities.length > 0 && (
                      <p className="ac-secondary">
                        {t("academy.placement.priorities", {
                          list: placement.plan.priorities.map((s) => t(`academy.skill.${s}`)).join(", "),
                        })}
                      </p>
                    )}
                    {placement.plan.mockEveryWeeks > 0 && (
                      <p className="ac-secondary">{t("academy.placement.mockEvery", { count: placement.plan.mockEveryWeeks })}</p>
                    )}
                  </div>
                )}
                <p className="ac-footnote">{t("academy.estimateNotice")}</p>
                {placementAttempt?.report_url && (
                  <a className="ac-more" href={placementAttempt.report_url} target="_blank" rel="noreferrer">
                    <FileDown aria-hidden size={16} />
                    {t("academy.placement.guide")}
                  </a>
                )}
              </>
            ) : (
              <p className="ac-secondary">{t("academy.placement.preparing")}</p>
            )}
          </GlassCard>
        )}
      </div>

      <section className="ac-section" aria-labelledby="academy-train">
        <SectionTitle id="academy-train">{t("academy.train.title")}</SectionTitle>
        {locked && (
          <p className="ac-note" role="note">
            {t("academy.train.locked")}
          </p>
        )}
        <div className="ac-grid">
          <GlassCard as="article" className="ac-card ac-train ac-span-6" aria-labelledby="ac-train-mock">
            <div className="ac-train__icon ac-tone--accent" aria-hidden="true">
              <ClipboardList size={22} strokeWidth={1.8} />
            </div>
            <h3 id="ac-train-mock" className="ac-title-3">
              {t("academy.train.mock")}
            </h3>
            <p className="ac-secondary">{t("academy.train.mockHint")}</p>
            <div className="ac-train__skills" aria-hidden="true">
              {SKILLS.map((s) => (
                <SkillIcon key={s} skill={s} size={15} />
              ))}
              {mockMinutes > 0 && (
                <span className="ac-caption">
                  <TimerIcon size={13} /> {t("academy.ui.home.mockLength", { minutes: mockMinutes })}
                </span>
              )}
            </div>
            <div className="ac-card__actions">
              <Link className={buttonClass("ghost")} to="/academy/mocks">
                {t("academy.mocks.seeAll")}
              </Link>
              <Button variant="tinted" disabled={locked || pending} onClick={() => start("mock", [...SKILLS], "exam")}>
                {t("academy.train.startMock")}
              </Button>
            </div>
          </GlassCard>
          <GlassCard as="article" className="ac-card ac-train ac-span-6" aria-labelledby="ac-train-practice">
            <h3 id="ac-train-practice" className="ac-title-3">
              {t("academy.train.practice")}
            </h3>
            <fieldset className="ac-skill-picker" disabled={locked}>
              <legend className="ac-label">{t("academy.train.skill")}</legend>
              {SKILLS.map((s) => (
                <label key={s} className={`ac-skill-option ac-skill--${s}${practiceSkill === s ? " ac-skill-option--on" : ""}`}>
                  <input
                    type="radio"
                    name="ac-practice-skill"
                    value={s}
                    checked={practiceSkill === s}
                    onChange={(e) => isSkill(e.target.value) && setPracticeSkill(e.target.value)}
                  />
                  <SkillIcon skill={s} size={16} />
                  <span>{t(`academy.skill.${s}`)}</span>
                </label>
              ))}
            </fieldset>
            <SegmentedControl<AttemptModeEnum>
              label={t("academy.train.mode")}
              value={practiceMode}
              options={[
                { value: "training", label: t("academy.mode.training") },
                { value: "exam", label: t("academy.mode.exam") },
              ]}
              onChange={(value) => setPracticeMode(value)}
            />
            <p className="ac-footnote">{t(`academy.mode.${practiceMode}Hint`)}</p>
            <div className="ac-card__actions">
              <Button variant="secondary" disabled={locked || pending} onClick={() => start("practice", [practiceSkill], practiceMode)}>
                {t("academy.train.startPractice")}
              </Button>
            </div>
          </GlassCard>
        </div>
        {error !== null && <p className="ko">{error || t("common.error")}</p>}
      </section>

      <PlansSection me={learner} compact={!locked} />

      <section className="ac-section" aria-labelledby="academy-history">
        <SectionTitle id="academy-history">{t("academy.history.title")}</SectionTitle>
        {attempts.isLoading && <Skeleton style={{ height: 72 }} />}
        {attempts.isSuccess && rows.length === 0 && (
          <div className="ac-surface ac-empty">
            <ClipboardList aria-hidden size={22} />
            <p className="ac-secondary">{t("academy.history.empty")}</p>
          </div>
        )}
        {rows.length > 0 && (
          <ul className="ac-list ac-surface">
            {rows.map((attempt) => {
              const skills = Array.isArray(attempt.skills) ? attempt.skills.filter(isSkill) : [];
              const open = attempt.status === "in_progress";
              const lead = skills.length === 1 && skills[0] ? skills[0] : null;
              return (
                <li key={attempt.id}>
                  <Link
                    className="ac-list__row"
                    to={open ? `/academy/attempt/${attempt.id}` : `/academy/attempt/${attempt.id}/results`}
                  >
                    {lead ? (
                      <SkillIcon skill={lead} size={16} />
                    ) : (
                      <span className="ac-pastille ac-tone--accent" aria-hidden="true">
                        <ClipboardList size={16} />
                      </span>
                    )}
                    <span className="ac-list__body">
                      <span className="ac-list__title">
                        {t(`academy.kind.${attempt.kind}`)}
                        {attempt.kind === "practice" && skills[0] ? ` · ${t(`academy.skill.${skills[0]}`)}` : ""}
                      </span>
                      <span className="ac-list__meta">
                        {dateText(attempt.submitted_at ?? attempt.created_at, i18n.language)} · {t(`academy.status.${attempt.status}`)}
                        {attempt.mode === "training" ? ` · ${t("academy.mode.training")}` : ""}
                      </span>
                      {!open && <SkillChips results={parseResults(attempt.results)} />}
                    </span>
                    <span className={`ac-list__action${open ? " ac-list__action--open" : ""}`}>
                      {open ? t("academy.history.resume") : t("academy.history.see")}
                      <ChevronRight aria-hidden size={16} />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
